import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaArrowLeft, FaBan, FaUserSlash, FaTrashAlt, 
  FaChevronRight, FaSignOutAlt, FaCopy // Added FaCopy
} from 'react-icons/fa';
import { MdOutlineDeleteForever } from 'react-icons/md';
import { jwtDecode } from "jwt-decode";
import * as api from "../../api/index";

const Settings = ({refreshAuth}) => {
  const navigate = useNavigate();

  // --- NEW: Handle Copy Shop Link ---
  const handleCopyShopLink = () => {
    const token = localStorage.getItem("token");
    if (!token) {
      alert("Please log in to copy your shop link.");
      return;
    }

    try {
      const decoded = jwtDecode(token);
      const userId = decoded.id || decoded._id;
      // This matches your deep linking path: /shop/:id
      const shopUrl = `https://ourasap.com/shop/${userId}`;
      
      navigator.clipboard.writeText(shopUrl);
      alert("Your shop link is ready to share.");
    } catch (err) {
      console.error(err);
      alert("Failed to copy link.");
    }
  };

  // Handle Logout
  // Optimized Logout for Web
  const handleLogout = () => {
    // 1. Confirm with the user
    const confirmLogout = window.confirm("Are you sure you want to log out?");
    if (!confirmLogout) return;

    // 2. Clear ALL local data (Token, User Info, etc.)
    localStorage.clear();

    // 4. Trigger the Auth Refresh (Very Important!)
    // This resets your global state (userId, name, etc.) to null immediately
    if (typeof refreshAuth === "function") {
      refreshAuth();
    }

    // 5. Nuclear Option: Force a hard redirect or reset navigation stack
    // 'replace: true' prevents the user from clicking "Back" to get to Settings
    navigate('/auth', { replace: true });

    // 6. Pro Tip: Hard reload the page to purge any leftover memory leaks/states
    // window.location.reload(); 
  };

  const handleDeleteAccount = async () => {
    const confirmDelete = window.confirm(
      "DANGER: This action is permanent. All your data, shop info, and history will be deleted forever. You will NOT be able to log back in. Are you sure?"
    );

    if (confirmDelete) {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;

        const decoded = jwtDecode(token);
        const userId = decoded.id || decoded._id;

        // 1. Call API to delete from Database
        const res = await api.deleteAccount(userId);

        if (res.data.success || res.status === 200) {
          // 2. Clear every trace of the session
          localStorage.clear();
          sessionStorage.clear();

          // 3. Clear the global React state (if refreshAuth exists)
          if (typeof refreshAuth === "function") refreshAuth();

          alert("Your account has been permanently deleted.");

          // 4. Redirect and replace history (kills the back button)
          navigate('/auth', { replace: true });

          // 5. Hard reload to reset the entire JS environment
          window.location.reload(); 
        }
      } catch (err) {
        console.error(err);
        alert("Failed to delete account. You may need to log in again first.");
      }
    }
  };

  const SettingItem = ({ icon, label, onClick, isDanger = false }) => (
    <button
      onClick={onClick}
      className="w-full flex items-center justify-between p-4 transition-all border-b border-gray-50 last:border-0 hover:bg-gray-50 active:bg-gray-100 group"
    >
      <div className="flex items-center space-x-4">
        <div className={`p-2.5 rounded-xl flex items-center justify-center transition-colors ${
          isDanger ? 'bg-red-50 text-red-500' : 'bg-gray-100 text-teal-600 group-hover:bg-teal-50'
        }`}>
          {icon}
        </div>
        <span className={`font-bold text-[15px] ${isDanger ? 'text-red-500' : 'text-gray-700'}`}>
          {label}
        </span>
      </div>
      <FaChevronRight 
        size={14} 
        className={`${isDanger ? 'text-red-300' : 'text-gray-300'} group-hover:translate-x-1 transition-transform`} 
      />
    </button>
  );

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      {/* Header */}
      <div className="sticky top-0 z-10 flex items-center justify-between bg-white px-6 py-5 border-b border-gray-100 shadow-sm">
        <button 
          onClick={() => navigate(-1)} 
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <FaArrowLeft size={20} className="text-gray-700" />
        </button>
        <h1 className="text-lg font-black text-gray-900 tracking-tight">Settings</h1>
        <div className="w-10" /> 
      </div>

      <div className="max-w-xl mx-auto p-6">
        
        {/* Share Section */}
        <section className="mb-8">
          <h2 className="text-[11px] font-black text-gray-400 uppercase tracking-[0.15em] mb-4 ml-2">
            Share & Promote
          </h2>
          <div className="bg-white rounded-[28px] shadow-sm border border-gray-100 overflow-hidden">
            <SettingItem 
              icon={<FaCopy size={16} />} 
              label="Copy My Shop Link" 
              onClick={handleCopyShopLink} 
            />
          </div>
        </section>

        {/* Privacy Section */}
        <section className="mb-8">
          <h2 className="text-[11px] font-black text-gray-400 uppercase tracking-[0.15em] mb-4 ml-2">
            Privacy & Content
          </h2>
          <div className="bg-white rounded-[28px] shadow-sm border border-gray-100 overflow-hidden">
            <SettingItem 
              icon={<FaBan size={16} />} 
              label="Blocked Products" 
              onClick={() => navigate("/blocked-products")} 
            />
            <SettingItem 
              icon={<FaUserSlash size={16} />} 
              label="Blocked Users" 
              onClick={() => navigate("/blocked-users")} 
            />
          </div>
        </section>

        {/* Account Actions Section */}
        <section className="mb-8">
          <h2 className="text-[11px] font-black text-gray-400 uppercase tracking-[0.15em] mb-4 ml-2">
            Account Actions
          </h2>
          <div className="bg-white rounded-[28px] shadow-sm border border-gray-100 overflow-hidden">
            <SettingItem 
              icon={<FaSignOutAlt size={16} />} 
              label="Log Out" 
              onClick={handleLogout} 
            />
            <SettingItem 
              icon={<MdOutlineDeleteForever size={20} />} 
              label="Delete Account" 
              isDanger={true}
              onClick={handleDeleteAccount} 
            />
          </div>
        </section>

        {/* Branding/Version */}
        <div className="flex flex-col items-center justify-center mt-12 space-y-2">
          <div className="w-12 h-1 bg-gray-200 rounded-full mb-2" />
          <p className="text-[10px] font-bold text-gray-300 uppercase tracking-widest">OurASAP v1.0.0</p>
        </div>
      </div>
    </div>
  );
};

export default Settings;